import React, { useEffect, useState, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { SpinnerIcon } from "@livekit/components-react";
import { isIOS, isMobileSafari } from "react-device-detect";
import IntraverseMiniLogoIcon from "../../assets/images/logos/pink_intraverse_logo.svg";
import uiBgLoadingVagonDev from "../../assets/images/backgrounds/UI_BG_Loading_Vagon_Dev.png";
import uiBgLoadingVagonStaging from "../../assets/images/backgrounds/UI_BG_Loading_Vagon_Staging.png";
import uiBgLoadingVagon from "../../assets/images/backgrounds/UI_BG_Loading_Vagon.png";
import { useDCVStreaming } from "../../renderStreaming/dcvStreamingHook2";
import { getLoaderProgress } from "../../utils/generalUtils";

const bgImages = {
    production: uiBgLoadingVagon,
    staging: uiBgLoadingVagonStaging,
    development: uiBgLoadingVagonDev,
};

const TZStreaming = ({ roomId }) => {
    const [selectedDevices, setSelectedDevices] = useState({ audioOutput: "", audioInput: "", webcamInput: "" });
    const [devices, setDevices] = useState({
        audioOutput: [{ deviceId: "", label: "Default Output" }],
        audioInput: [{ deviceId: "", label: "Default Input" }],
        webcamInput: [{ deviceId: "", label: "No webcam" }],
    });
    const [showClickToast, setShowClickToast] = useState(false);
    const {
        dcvDisplayRef,
        onJoinButtonClicked,
        isConnected,
        firstFrameReceived,
        enableDevices,
        dcvConnectionRef,
        loaderText,
        streamingUrl,
        postRequestResolution,
        iframeReady,
        pollingElapsedSeconds,
    } = useDCVStreaming(roomId, selectedDevices);
    const [showLoader, setShowLoader] = useState(false);
    const [joined, setJoined] = useState(false);
    const audioInterfaceRef = useRef(null);
    const loaderRef = useRef(null);
    const progress = getLoaderProgress(loaderText, pollingElapsedSeconds);
    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (progress / 100) * circumference;
    const ua = navigator.userAgent.toLowerCase();
    const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
    const isFirefox = ua.includes("firefox");
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const [isBrave, setIsBrave] = useState(false);
    useEffect(() => {
        const detectBraveAndInit = async () => {
            let braveDetected = false;
            if (navigator.brave && typeof navigator.brave.isBrave === "function") {
                braveDetected = await navigator.brave.isBrave();
            }
            setIsBrave(braveDetected);
            await initializeDevices();
        };
        detectBraveAndInit();
    }, []);

    // Manual width/height controls for aspect box and iframe
    const [manualSize, setManualSize] = useState({ width: 800, height: 450 });

    // Handler for manual size changes
    const handleManualSizeChange = (type, delta) => {
        setManualSize((prev) => {
            const newSize = {
                ...prev,
                [type]: Math.max(100, prev[type] + delta),
            };
            // Update DOM styles directly
            const aspectBox = document.getElementById("dcv-aspect-box");
            const iframe = document.getElementById("dcv-iframe");
            if (aspectBox) {
                aspectBox.style.width = `${newSize.width}px`;
                aspectBox.style.height = `${newSize.height}px`;
            }
            if (iframe) {
                iframe.style.width = `${newSize.width}px`;
                iframe.style.height = `${newSize.height}px`;
            }
            return newSize;
        });
    };
    const applyAudioOutput = async (deviceId) => {
        if (!deviceId) return;
        // ONLY Chromium-based browsers support setSinkId
        const ua = navigator.userAgent.toLowerCase();
        const isFirefox = ua.includes("firefox");
        const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
        const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
        let isBrave = false;
        if (navigator.brave && typeof navigator.brave.isBrave === "function") {
            isBrave = await navigator.brave.isBrave();
        }
        if (!isChrome && !isSafari && !isBrave) return; // skip unsupported browsers
        const audioEls = document.getElementsByTagName("audio");
        await Promise.all(
            Array.from(audioEls).map((audioEl) => {
                if (typeof audioEl.setSinkId === "function") {
                    return audioEl.setSinkId(deviceId).catch((err) => console.warn("Audio output not supported:", err));
                }
                return Promise.resolve();
            })
        );
    };
    const initializeDevices = useCallback(async () => {
        try {
            // get basic media (audio + video)
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            } catch {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
            await new Promise((resolve) => setTimeout(resolve, 100)); // Firefox/Brave fix
            const deviceList = await navigator.mediaDevices.enumerateDevices();
            const audioInput = [{ deviceId: "", label: "Default Input" }];
            const webcamInput = [{ deviceId: "", label: "No webcam" }];
            const audioOutput = [{ deviceId: "", label: "Default Output" }];
            deviceList.forEach((device) => {
                if (device.kind === "audioinput")
                    audioInput.push({ deviceId: device.deviceId, label: device.label || "Mic" });
                if (device.kind === "videoinput")
                    webcamInput.push({ deviceId: device.deviceId, label: device.label || "Camera" });
                if (device.kind === "audiooutput")
                    audioOutput.push({ deviceId: device.deviceId, label: device.label || "Speakers" });
            });
            if (isFirefox) {
                // Only audio input + webcam
                setDevices({ audioOutput: [], audioInput, webcamInput });
                setSelectedDevices((prev) => ({
                    ...prev,
                    audioInput: audioInput[0].deviceId,
                    webcamInput: webcamInput[0].deviceId,
                }));
            } else if (isChrome || isSafari || isBrave) {
                // Audio input + output + webcam
                setDevices({ audioOutput, audioInput, webcamInput });
                setSelectedDevices((prev) => ({
                    ...prev,
                    audioOutput: audioOutput[0].deviceId,
                    audioInput: audioInput[0].deviceId,
                    webcamInput: webcamInput[0].deviceId,
                }));
            }
            stream.getTracks().forEach((track) => track.stop());
        } catch (err) {
            console.warn("Error initializing devices:", err);
        }
    }, [isChrome, isFirefox, isSafari, isBrave]);
    useEffect(() => {
        const detectBraveAndInit = async () => {
            let brave = false;
            if (navigator.brave && typeof navigator.brave.isBrave === "function") {
                brave = await navigator.brave.isBrave();
            }
            // assign it to your state if needed
            initializeDevices();
        };
        detectBraveAndInit();
    }, []);
    const handleAudioOutputChange = async (deviceId) => {
        setSelectedDevices((prev) => ({ ...prev, audioOutput: deviceId }));
        if (!deviceId) return;
        const ua = navigator.userAgent.toLowerCase();
        const isFirefox = ua.includes("firefox");
        if (isFirefox) return;
        if (audioInterfaceRef.current) {
            const audioEls = audioInterfaceRef.current.getElementsByTagName("audio");
            await Promise.all(
                Array.from(audioEls).map((audioEl) => {
                    if (typeof audioEl.setSinkId === "function") {
                        return audioEl
                            .setSinkId(deviceId)
                            .catch((err) => console.warn("Audio output device not supported:", err));
                    }
                    return Promise.resolve();
                })
            );
        }
    };

    // Use JS-calculated width/height for perfect 16:9 fit and black bands
    const dcvWrapperRef = useRef(null);
    const [aspectBoxSize, setAspectBoxSize] = useState({ width: 0, height: 0, rotate: false });
    // Controls state for DcvAspectBox
    const [boxStyles, setBoxStyles] = useState({
        width: 0,
        height: 0,
        padding: 0,
        margin: 0,
        maxWidth: 0,
        maxHeight: 0,
    });
    const [showRotateToast, setShowRotateToast] = useState(false);
    const lastWasPortrait = useRef(false);

    const updateAspectBoxSize = useCallback(() => {
        const winW = window.innerWidth;
        let winH = window.innerHeight;
        // iOS Safari workaround for viewport height
        if (isIOS && isMobileSafari) {
            winH = document.documentElement.clientHeight;
        }
        const aspectW = 16;
        const aspectH = 9;
        let rotate = false;
        let width;
        let height;
        const isPortrait = winW < 768 && winH > winW;
        if (isPortrait) {
            if (!lastWasPortrait.current) {
                setShowRotateToast(true);
                setTimeout(() => setShowRotateToast(false), 8000);
            }
            lastWasPortrait.current = true;
            rotate = true;
            const coverH = winW;
            const coverW = Math.ceil((coverH * aspectW) / aspectH);
            width = coverW;
            height = coverH;
        } else {
            lastWasPortrait.current = false;
            if (showRotateToast) setShowRotateToast(false);
            height = winH;
            width = Math.floor((height * aspectW) / aspectH);
            if (width > winW) {
                width = winW;
                height = Math.floor((width * aspectH) / aspectW);
            }
        }
        setAspectBoxSize({ width, height, rotate });
        setBoxStyles((prev) => ({
            ...prev,
            width,
            height,
            maxWidth: width,
            maxHeight: height,
        }));
        // ...existing code...
        if (dcvDisplayRef && dcvDisplayRef.current && dcvDisplayRef.current.contentWindow) {
            dcvDisplayRef.current.contentWindow.postMessage(
                { type: "dcv:resize", width, height },
                window.location.origin
            );
        }

        // Set the iframe size for visual consistency
        if (dcvDisplayRef && dcvDisplayRef.current) {
            // For iOS Safari, use viewport height workaround
            if (isIOS && isMobileSafari) {
                dcvDisplayRef.current.style.width = `${winW}px`;
                dcvDisplayRef.current.style.height = `${winH}px`;
            } else {
                dcvDisplayRef.current.style.width = `${width}px`;
                dcvDisplayRef.current.style.height = `${height}px`;
            }
            dcvDisplayRef.current.focus();
        }

        // Request DCV resolution if connected
        if (
            dcvConnectionRef &&
            dcvConnectionRef.current &&
            typeof dcvConnectionRef.current.requestResolution === "function"
        ) {
            dcvConnectionRef.current.requestResolution(width, height);
        }
    }, [dcvDisplayRef, dcvConnectionRef, showRotateToast]);

    useEffect(() => {
        updateAspectBoxSize();
        window.addEventListener("resize", updateAspectBoxSize);
        window.addEventListener("orientationchange", updateAspectBoxSize);
        return () => {
            window.removeEventListener("resize", updateAspectBoxSize);
            window.removeEventListener("orientationchange", updateAspectBoxSize);
        };
    }, [updateAspectBoxSize]);

    useEffect(() => {
        function onIframeConnected(e) {
            if (e.origin !== window.location.origin) return;
            const msg = e.data || {};
            if (msg.type === "dcv:connected") {
                // Ensure DCV container gets correct dimensions
                updateAspectBoxSize();
            }
        }
        window.addEventListener("message", onIframeConnected);
        return () => window.removeEventListener("message", onIframeConnected);
    }, [updateAspectBoxSize]);

    // Ensure DCV resolution is set on first load as soon as aspectBoxSize is available and iframe is ready
    useEffect(() => {
        if (
            aspectBoxSize.width > 0 &&
            aspectBoxSize.height > 0 &&
            typeof postRequestResolution === "function" &&
            iframeReady
        ) {
            postRequestResolution(aspectBoxSize.width, aspectBoxSize.height);
        }
    }, [aspectBoxSize, iframeReady, postRequestResolution]);

    useEffect(() => {
        const setVh = () => {
            let vh = window.innerHeight * 0.01;
            // iOS Safari workaround
            if (isIOS && isMobileSafari) {
                vh = document.documentElement.clientHeight * 0.01;
            }
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        };
        setVh();
        window.addEventListener("resize", setVh);
        window.addEventListener("orientationchange", setVh);
        return () => {
            window.removeEventListener("resize", setVh);
            window.removeEventListener("orientationchange", setVh);
        };
    }, []);

    // Show click toast when DCVDisplay is rendered (isConnected true)
    useEffect(() => {
        if (isConnected) {
            setShowClickToast(true);
            const timer = setTimeout(() => setShowClickToast(false), 5000);
            return () => clearTimeout(timer);
        }
    }, [isConnected]);

    const handleJoin = async () => {
        setShowLoader(true);
        setJoined(true);
        // Recalculate and resend aspect ratio/size to iframe when loader is triggered
        updateAspectBoxSize();
        onJoinButtonClicked();
        const waitForConnection = () =>
            new Promise((resolve) => {
                const check = setInterval(() => {
                    if (dcvConnectionRef.current) {
                        clearInterval(check);
                        resolve(true);
                    }
                }, 100);
            });

        await waitForConnection();
        await enableDevices(selectedDevices);
        if (!isFirefox) {
            await applyAudioOutput(selectedDevices.audioOutput);
        }
    };

    // Plus/Minus control panel logic
    const CONTROL_CONFIG = [
        { key: "width", label: "Width" },
        { key: "height", label: "Height" },
    ];
    const STEP = 8;
    const handleControlChange = (key, delta) => {
        if (key === "width" || key === "height") {
            setAspectBoxSize((prev) => {
                const newValue = Math.max(0, (prev[key] || 0) + delta);
                return { ...prev, [key]: newValue };
            });
            setBoxStyles((prev) => ({
                ...prev,
                [key]: Math.max(0, (aspectBoxSize[key] || 0) + delta),
            }));
        } else {
            setBoxStyles((prev) => {
                const newValue = Math.max(0, (prev[key] || 0) + delta);
                return { ...prev, [key]: newValue };
            });
        }
    };

    // Minimize/Expand state for panel
    const [panelMinimized, setPanelMinimized] = useState(false);
    const handlePanelToggle = () => setPanelMinimized((v) => !v);

    return (
        <>
            {showRotateToast && <RotateToastBanner>Rotate your screen to landscape mode</RotateToastBanner>}
            {showClickToast && (
                <LoadingToastBanner aspectBoxSize={aspectBoxSize}>
                    Loading Intraverse...Please wait <SpinningIcon style={{ marginLeft: "2rem" }} />
                </LoadingToastBanner>
            )}
            <BgContainer joined={joined} isConnected={isConnected} id="bg-container">
                {/* Join/Loader screens remain unchanged, with backdrop image */}
                {!joined && !isConnected && (
                    <>
                        <Logo
                            style={{
                                zIndex: 10000,
                                position: "absolute",
                                top: 10,
                                left: 30,
                                height: "50px",
                                width: "50px",
                            }}
                            src={IntraverseMiniLogoIcon}
                            alt="Logo"
                            onClick={() => window.location.reload()}
                        />
                        <AudioInterface id="audio-interface" ref={audioInterfaceRef}>
                            <Logo src={IntraverseMiniLogoIcon} alt="Logo" />
                            <DeviceSection>
                                <h2>Audio</h2>
                                <SelectWrapper>
                                    <DeviceSelect
                                        id="audioOutput"
                                        value={selectedDevices.audioOutput}
                                        onChange={(e) =>
                                            setSelectedDevices({ ...selectedDevices, audioOutput: e.target.value })
                                        }
                                    >
                                        {devices.audioOutput.map((d) => (
                                            <option key={d.deviceId} value={d.deviceId}>
                                                {d.label}
                                            </option>
                                        ))}
                                    </DeviceSelect>
                                </SelectWrapper>
                            </DeviceSection>
                            <DeviceSection>
                                <h2>Webcam</h2>
                                <SelectWrapper>
                                    <DeviceSelect
                                        id="webcamInput"
                                        value={selectedDevices.webcamInput}
                                        onChange={(e) =>
                                            setSelectedDevices({ ...selectedDevices, webcamInput: e.target.value })
                                        }
                                    >
                                        {devices.webcamInput.map((d) => (
                                            <option key={d.deviceId} value={d.deviceId}>
                                                {d.label}
                                            </option>
                                        ))}
                                    </DeviceSelect>
                                </SelectWrapper>
                            </DeviceSection>
                            <DeviceSection>
                                <h2>Microphone</h2>
                                <SelectWrapper>
                                    <DeviceSelect
                                        id="audioInput"
                                        value={selectedDevices.audioInput}
                                        onChange={(e) =>
                                            setSelectedDevices({ ...selectedDevices, audioInput: e.target.value })
                                        }
                                    >
                                        {devices.audioInput.map((d) => (
                                            <option key={d.deviceId} value={d.deviceId}>
                                                {d.label}
                                            </option>
                                        ))}
                                    </DeviceSelect>
                                </SelectWrapper>
                            </DeviceSection>
                            <JoinButton onClick={handleJoin} id="join-button">
                                Join Now
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z" />
                                </svg>
                            </JoinButton>
                        </AudioInterface>
                    </>
                )}
                {showLoader && !isConnected && (
                    <Loader
                        showLoader={showLoader}
                        isConnected={isConnected}
                        id="loader"
                        ref={loaderRef}
                        className={showLoader ? "" : "hidden"}
                    >
                        <img src={IntraverseMiniLogoIcon} alt="Intraverse Logo" />
                        <StyledStatusText id="statusText">{loaderText}</StyledStatusText>

                        <ProgressContainer>
                            <CircularProgress>
                                <ProgressCircle cx="90" cy="90" r="80" dashOffset={dashOffset} />
                            </CircularProgress>

                            <ProgressValue>
                                {progress}
                                <PercentSign>%</PercentSign>
                            </ProgressValue>
                        </ProgressContainer>
                    </Loader>
                )}
                {/* DCV Game Display: always full screen, aspect fit, black bars, rotates on portrait mobile */}
                <DcvOuterWrapper
                    ref={dcvWrapperRef}
                    style={{
                        background: isConnected ? "black" : "none",
                        display: isConnected ? "flex" : "none",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <DcvAspectBox
                        id="dcv-aspect-box"
                        aspectBoxSize={aspectBoxSize}
                        style={{
                            width: manualSize.width,
                            height: manualSize.height,
                            minWidth: 0,
                            minHeight: 0,
                            background: "black",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            position: "relative",
                        }}
                    >
                        <DcvDisplay
                            ref={dcvDisplayRef}
                            id="dcv-iframe"
                            src="/dcv-iframe.html"
                            allow="autoplay; camera; microphone; fullscreen; clipboard-read; clipboard-write"
                            style={{ width: "100%", height: "100%", border: 0 }}
                            isIphoneSafari={isIOS && isMobileSafari}
                        />
                    </DcvAspectBox>
                </DcvOuterWrapper>
            </BgContainer>
        </>
    );
};

export default TZStreaming;

const BgContainer = styled.div`
    background-image: url(${bgImages[process.env.REACT_APP_STREAM_API_ENV] || uiBgLoadingVagonDev});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    background-color: black;
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    position: fixed;
    top: 0;
    left: 0;
    overflow: hidden;

    @media (max-width: 900px) {
        background-size: cover;
    }

    @media (orientation: portrait) and (max-width: 768px) {
        ${(props) =>
            !props.joined &&
            !props.isConnected &&
            `transform: rotate(-90deg) translateX(-100vh);
        transform-origin: top left;
        top: 0;
        left: 0;
        width: 100vh;
        height: 100vw;`}
    }
`;

const DcvDisplay = styled.iframe`
    width: 100vw;
    height: 100vh;
    position: relative;
    z-index: 11;
    display: "block";
    /* display: ${(props) => (props.active ? "block" : "none")}; */
    background: black;
    ${(props) => props.isIphoneSafari && `height: calc(var(--vh, 1vh) * 100);`}
    canvas,
    video {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
        display: block !important;
    }
`;

const DcvOuterWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 10;
    overflow: hidden;
    transition: background 0.2s;
`;

const DcvAspectBox = styled.div`
    /* position: absolute; */
    ${(props) => props.aspectBoxSize.rotate && `position: absolute;`}
    ${(props) => props.aspectBoxSize.rotate && `top: 50%;`}
    ${(props) => props.aspectBoxSize.rotate && `left: 50%;`}
    right: 0;
    bottom: 0;
    /* ${(props) => !props.aspectBoxSize.rotate && `margin: auto;`} */
    ${(props) => !props.aspectBoxSize.rotate && `max-width: 100vw;`}
    ${(props) => !props.aspectBoxSize.rotate && `max-height: 100vh;`}
    ${(props) =>
        props.aspectBoxSize.rotate &&
        `transform: translate(-50%, -50%) rotate(-90deg); transform-origin: center center;`}

    width: ${(props) => props.aspectBoxSize.width}px !important;
    height: ${(props) => props.aspectBoxSize.height}px !important;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SpinningIcon = styled(SpinnerIcon)`
    animation: ${spin} 3s linear infinite;
`;

const LoadingToastBanner = styled.div`
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 0;
    ${(props) =>
        props.aspectBoxSize.rotate
            ? `top: auto; bottom: 0; width: 100vh; height: 100vw; transform: rotate(-90deg); transform-origin: bottom left; background: url(${uiBgLoadingVagon}) no-repeat left bottom;`
            : `top: 0; width: 100vw; height: 100vh; background: url(${uiBgLoadingVagon}) no-repeat center;`}
    background-size: cover;
    color: white;
    text-align: center;
    font-size: 1.1rem;
    font-weight: 600;
    padding: 16px 0;
    z-index: 9999;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    user-select: none;
`;

const AudioInterface = styled.div`
    color: white;
    padding: 15px;
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.8);
    width: 90%;
    max-width: 380px;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border: 1px solid #333;
    overflow-y: auto;

    &.hidden {
        display: none !important;
    }

    @media (max-height: 600px) and (orientation: landscape) {
        height: 100vh;
        max-height: 100vh;
        padding: 12px;
        font-size: 13px;
        padding-top: 0.3rem;
        border-radius: 3px;
        h2 {
            font-size: 14px;
        }
        select,
        button {
            font-size: 12px;
            padding: 4px;
            margin: 3px;
        }
    }
`;

const Logo = styled.img`
    width: 60px;
    height: 60px;
    flex-shrink: 0;

    @media (max-height: 600px) and (orientation: landscape) {
        width: 30px;
        height: 30px;
    }
`;

const RotateToastBanner = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    background: #beaeff;
    color: #222;
    text-align: center;
    font-size: 1.1rem;
    font-weight: 600;
    padding: 16px 0;
    z-index: 9999;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    user-select: none;
`;

const DeviceSection = styled.div`
    width: 100%;
    margin-bottom: 12px;
    flex-shrink: 0;

    h2 {
        text-align: left;
        margin-bottom: 5px;
        font-size: 16px;
    }

    @media (max-height: 600px) and (orientation: landscape) {
        margin-bottom: 6px;
        h2 {
            margin-bottom: 5px;
        }
    }
`;

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;

    &::after {
        content: "▼";
        font-size: 0.8rem;
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        color: #666;
    }
`;

const DeviceSelect = styled.select`
    width: 100%;
    padding: 10px;
    background-color: black;
    border: 1px solid #444;
    border-radius: 8px;
    color: white;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    margin-bottom: 8px;
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: #666;
    }
`;

const JoinButton = styled.button`
    width: 100%;
    padding: 12px;
    background-color: #23d3fb;
    border: none;
    border-radius: 9999px;
    color: black;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    flex-shrink: 0;

    &:hover {
        background-color: #1fb1fd;
    }
`;

const Loader = styled.div`
    color: white;
    font-size: 20px;
    padding: 25px;
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.8);
    width: 90%;
    max-width: 400px;
    height: auto;
    min-height: 280px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border: 1px solid #968dfd;

    &.hidden {
        display: none !important;
    }

    img {
        display: block;
        align-self: center;
        width: 50px;
        height: auto;
        margin-bottom: 20px;
    }

    p {
        color: #ced1d9;
        font-size: 16px;
        margin: 20px 0;
    }

    @media (max-height: 600px) and (orientation: landscape) {
        padding: 15px;
        font-size: 13px;
        border-radius: 3px;
        min-height: 0;
        max-height: 100vh;
        img {
            width: 28px;
            margin-bottom: 8px;
        }
        p {
            font-size: 13px;
            margin: 8px 0;
        }
    }

    @media (orientation: portrait) and (max-width: 768px) {
        ${(props) =>
            props.showLoader &&
            !props.isConnected &&
            `top: 50vw;
        left: 50vw;
        width: 100vh;
        height: 100vw;
        position: fixed;
        transform: rotate(-90deg) translate(-100vw, -50vw);
        transform-origin: top left;
        z-index: 99999;
        `}
    }
`;
const ProgressContainer = styled.div`
    position: relative;
    width: 180px;
    height: 180px;
`;

const CircularProgress = styled.svg`
    transform: rotate(-90deg);
    width: 180px;
    height: 180px;
`;

const ProgressValue = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 32px;
    font-weight: 600;
    color: #fff;
    display: flex;
    align-items: baseline;
    margin-bottom: 2rem;
`;

const PercentSign = styled.span`
    font-size: 18px;
    margin-left: 4px;
`;

const StyledStatusText = styled.p`
    color: #ced1d9;
    font-size: 16px;
`;

const ProgressCircle = styled.circle`
    fill: none;
    stroke: #23d3fb;
    stroke-width: 10;
    stroke-linecap: round;
    stroke-dasharray: 502; /* circumference for r=80 */
    stroke-dashoffset: ${({ dashOffset }) => dashOffset};
    transition: stroke-dashoffset 0.3s ease-out; /* smooth animation */
`;

const ScaleControls = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    gap: 16px;
    z-index: 100;
    background: rgba(0, 0, 0, 0.6);
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    align-items: center;
    pointer-events: auto;
    user-select: none;
`;

const ScaleControlsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ScaleLabel = styled.span`
    color: #fff;
    font-weight: 600;
    font-size: 1.1rem;
`;

const ScaleButton = styled.button`
    background: #23d3fb;
    color: #222;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    font-size: 1.5rem;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    transition: background 0.2s;
    &:hover {
        background: #1fb1fd;
    }
`;

const ScaleInfo = styled.div`
    margin-top: 12px;
    color: #fff;
    font-size: 1rem;
    font-weight: 500;
    text-align: center;
`;
